import { Box, MenuItem, SelectChangeEvent, Stack, styled } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PoolImage, PoolLineClampTypography, PoolRow, PoolSelect, useIsMobile } from '@synergy/components-next';
import { ReactElement } from 'react';

import { VerticalDivider } from '../../../components/vertical-divider/vertical-divider';
import { useArticleDetailContext } from '../article-detail';

const StyledImage = styled(Box)(({ theme }) => ({
    width: theme.spacing(8),
    height: theme.spacing(8),
    backgroundColor: theme.palette.common.white,

    '& > img': {
        maxWidth: '100%',
        maxHeight: '100%',
    },
}));

export const ArticleDetailVariantsDropdown = (): ReactElement | null => {
    const { article, variants, onVariantSelected } = useArticleDetailContext();
    const isMobile = useIsMobile();

    if (!variants || variants.length <= 1) {
        return null;
    }

    const variantSelectOptions = variants.map(variant => ({
        value: variant.id,
        displayValue: variant.name,
    }));

    const handleSelectVariant = (event: SelectChangeEvent<number | '' | null>): void => {
        const value = event.target.value;
        if (value === null || value === article.id) {
            return;
        }
        onVariantSelected(+value);
    };

    return (
        <PoolSelect
            options={variantSelectOptions}
            fullWidth={true}
            onChange={handleSelectVariant}
            value={article.id}
            MenuProps={{
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                },
                transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                },
            }}
        >
            {variants.map(variant => (
                <MenuItem
                    sx={{ gap: 2, opacity: !variant.isActive ? 0.5 : undefined }}
                    value={variant.id}
                    key={variant.id}
                >
                    {!isMobile && (
                        <StyledImage>
                            <PoolImage src={variant.previewImage?.['70x70']} alt={variant.name} />
                        </StyledImage>
                    )}
                    <Stack gap={0.25}>
                        <PoolLineClampTypography variant="subtitle1">{variant.name}</PoolLineClampTypography>
                        <PoolRow typography="body2" color="grey.800">
                            <PoolRow divider={<VerticalDivider />}>
                                {variant.attributes?.map(attribute => (
                                    <span key={attribute.attributeId}>
                                        {attribute.name}
                                        &nbsp;
                                        {attribute.value}
                                    </span>
                                ))}
                            </PoolRow>
                            {!variant.isActive && (
                                <Box sx={{ fontStyle: 'oblique 15deg' }}>
                                    <FormattedMessage key={-1} id="app.article.detail.variant.notAvailable" />
                                </Box>
                            )}
                        </PoolRow>
                    </Stack>
                </MenuItem>
            ))}
        </PoolSelect>
    );
};
